import { ReactNode, useState } from "react";
import { useBoolean } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { useUpdateLikesMutation } from "src/api/client/like.api";
import { Button, ButtonProps } from "src/components/base/button";
import { LIKE_BUTTON_TEST_ID } from "src/components/common/buttons/like-button/like-button.test-ids";
import { EnumLikeableEntityType } from "src/components/common/buttons/like-button/like-button.types";
import { IconLikeFilled, IconLikeOutline } from "src/components/icons";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { useWfToast, EnumToastStatus } from "src/hooks/use-wf-toast";
import { useUserStore } from "src/stores/user-store";
import { ILikeInfo } from "src/types/common";

export interface IEntityReference {
    entityId: string;
    entityType: EnumLikeableEntityType;
}

export interface ILikeButtonProps extends Omit<ButtonProps, "children"> {
    likeInfo: ILikeInfo;
    entityReferences: IEntityReference[];
    onClick?: () => void;
    label?: string;
    children?: (likeCount: number) => ReactNode;
}

export const LikeButton = ({ entityReferences, likeInfo, label, onClick, disabled, children, ...buttonProps }: ILikeButtonProps) => {
    const { t } = useTranslation("common");
    const toast = useWfToast();
    const { openLoginModal } = useLinkBuilder();
    const { isLoggedIn } = useUserStore();
    const { formatNumber } = useNumberFormatter();

    const [isLoading, setLoading] = useBoolean();
    const [didLike, setDidLike] = useState(likeInfo.didLike);
    const [likeCount, setLikeCount] = useState(likeInfo.count);

    const updateLikes = useUpdateLikesMutation();

    const handleClick = async () => {
        if (!isLoggedIn) {
            openLoginModal();
            return;
        }

        try {
            setLoading.on();

            if (!didLike && onClick) {
                onClick();
            }

            await updateLikes({
                entityLikes: entityReferences.map(entity => ({
                    entityId: entity.entityId,
                    entityType: entity.entityType,
                    liked: !didLike,
                })),
            });

            setDidLike(!didLike);
            setLikeCount(!didLike ? likeCount + 1 : likeCount - 1);
        } catch {
            toast({ status: EnumToastStatus.Error, title: t("general-error") });
        } finally {
            setLoading.off();
        }
    };

    const getFormattedLabel = () => {
        if (label) {
            return likeCount ? `${label} (${formatNumber(likeCount)})` : label;
        }

        return likeCount ? formatNumber(likeCount) : undefined;
    };

    return (
        <Button
            isDisabled={isLoading || disabled}
            variant="ghost"
            px={2}
            leftIcon={didLike ? <IconLikeFilled boxSize={3} /> : <IconLikeOutline boxSize={3} />}
            onClick={handleClick}
            className={didLike ? "gtm-dislike" : "gtm-like"}
            data-gtm-action={`${entityReferences.map(entity => entity.entityType).join("-")} click`}
            data-test-id={LIKE_BUTTON_TEST_ID}
            {...buttonProps}
        >
            {children ? children(likeCount) : getFormattedLabel()}
        </Button>
    );
};
